import { useEffect, useRef, type ReactNode } from 'react';
import { Icon, Skeleton } from '@capsico/ui';
import { Button, Card, DraftBar, Sheet } from './ui';

/*
 * Live tracking kit (patterns C21–C25, W14). Presentation only: every value and callback comes
 * from the order page, which gets them from useLiveOrder / useOrderChat / useTrackExtras.
 */

/** Chat bubble glyph (line icon, same stroke grammar as @capsico/ui icons). */
function ChatGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12Z" />
      <path d="M8.5 11h7M8.5 14.5h4.5" />
    </svg>
  );
}

/** Map placeholder while MapLibre loads (and the frame the map sits in). */
export function MapSkeleton() {
  return <Skeleton className="h-60 w-full" rounded="card" />;
}

/** "Reconnecting… updating every 15 s" strip (C21) while the live socket is down. */
export function LiveBanner({ text }: { text: string }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2.5 rounded-xl bg-warning-bg px-3.5 py-2.5 text-xs font-medium text-ink"
    >
      <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-surface text-warning">
        <Icon name="refresh" size={14} />
      </span>
      <span className="min-w-0 flex-1">{text}</span>
    </div>
  );
}

/**
 * Status hero (C22): stage title 18/700, one plain line under it, the ETA block right
 * ("Arriving in" caption + "25–32 min" 20/700, or "Running late"), optional extra lines.
 */
export function TrackStatusCard({
  title,
  subtitle,
  etaCaption,
  etaValue,
  late,
  children,
}: {
  title: string;
  subtitle?: string | null;
  etaCaption?: string | null;
  etaValue?: string | null;
  late?: boolean;
  children?: ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lg leading-6 font-bold text-ink">{title}</p>
          {subtitle && <p className="mt-1 text-xs text-ink-2">{subtitle}</p>}
        </div>
        {etaValue && (
          <div
            className={`shrink-0 rounded-xl px-3 py-2 text-right ${late ? 'bg-warning-bg' : 'bg-brand-soft'}`}
          >
            {etaCaption && (
              <p className="text-micro font-medium text-ink-2 uppercase">{etaCaption}</p>
            )}
            <p className="text-lg leading-6 font-bold text-ink tabular">{etaValue}</p>
          </div>
        )}
      </div>
      {children}
    </Card>
  );
}

export interface StepView {
  key: string;
  label: string;
  time: string | null;
  done: boolean;
  current: boolean;
  negative?: boolean;
}

/** Vertical stepper (C23): done = yellow-600 dot with ink tick, current = haloed, rest = hollow. */
export function TrackStepper({ steps, label }: { steps: StepView[]; label: string }) {
  return (
    <ol className="mt-4 pt-4 shadow-[inset_0_1px_0_var(--color-hair)]" aria-label={label}>
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        const dot = step.negative
          ? 'bg-negative text-white'
          : step.done
            ? 'bg-brand-600 text-on-brand'
            : 'bg-surface shadow-[inset_0_0_0_1.5px_var(--color-control)]';
        return (
          <li key={step.key} className="relative flex gap-3 pb-3 last:pb-0">
            {!last && (
              <span
                aria-hidden="true"
                className={`absolute top-5 bottom-0 left-2.25 w-0.5 ${step.done && steps[index + 1]?.done ? 'bg-brand-600' : 'bg-hair'}`}
              />
            )}
            <span
              className={`relative mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full ${dot} ${
                step.current ? 'ring-4 ring-brand-pale' : ''
              }`}
            >
              {step.negative ? (
                <Icon name="x" size={12} />
              ) : step.done ? (
                <Icon name="check" size={12} />
              ) : null}
            </span>
            <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
              <p
                className={`text-sm ${step.current ? 'font-semibold text-ink' : step.done ? 'text-ink' : 'text-ink-3'}`}
              >
                {step.label}
              </p>
              {step.time && (
                <span className="shrink-0 pt-0.5 text-caption text-ink-3 tabular">{step.time}</span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Rider card (C24): 48px photo (or initial on brand-soft), name 15/700, rating ★ + vehicle
 * caption, and two round actions — masked call (yellow) and chat (outline, unread dot).
 */
export function RiderCard({
  name,
  photoUrl,
  ratingText,
  vehicleText,
  note,
  calling,
  chatEnabled,
  unread,
  onCall,
  onChat,
  labels,
}: {
  name: string;
  photoUrl: string | null;
  ratingText: string | null;
  vehicleText: string | null;
  note?: string | null;
  calling: boolean;
  chatEnabled: boolean;
  unread: number;
  onCall: () => void;
  onChat: () => void;
  labels: { call: string; chat: string; unread: string };
}) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="size-12 shrink-0 rounded-full bg-tile object-cover"
            loading="lazy"
          />
        ) : (
          <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-soft text-lg font-bold text-brand-ink">
            {name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-title font-bold text-ink">{name}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-caption text-ink-2">
            {ratingText && (
              <span className="inline-flex items-center gap-0.5 font-semibold text-warning">
                <Icon name="star" size={12} />
                {ratingText}
              </span>
            )}
            {vehicleText && <span className="tabular">{vehicleText}</span>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {chatEnabled && (
            <button
              type="button"
              onClick={onChat}
              aria-label={unread > 0 ? `${labels.chat} · ${labels.unread}` : labels.chat}
              className="relative inline-flex size-11 items-center justify-center rounded-full bg-surface text-ink shadow-[inset_0_0_0_1px_var(--color-control)] active:scale-95"
            >
              <ChatGlyph />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-4.5 items-center justify-center rounded-full bg-negative px-1 text-micro font-bold text-white tabular">
                  {unread}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onCall}
            disabled={calling}
            aria-label={labels.call}
            className="inline-flex size-11 items-center justify-center rounded-full bg-brand text-on-brand active:scale-95 disabled:bg-tile disabled:text-ink-3"
          >
            {calling ? (
              <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Icon name="phone" size={18} />
            )}
          </button>
        </div>
      </div>
      {note && <p className="mt-3 text-caption text-ink-3">{note}</p>}
    </Card>
  );
}

/** Delivery code (C25): 4 big digit tiles on brand-soft, hint under, lock notice when locked. */
export function DeliveryCodeCard({
  title,
  code,
  hint,
  lockedText,
  loading,
  failedText,
  onRetry,
  retryLabel,
}: {
  title: string;
  code: string | null;
  hint: string;
  lockedText: string | null;
  loading: boolean;
  failedText: string | null;
  onRetry: () => void;
  retryLabel: string;
}) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className="inline-flex size-8 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
          <Icon name="key" size={16} />
        </span>
        <h2 className="text-sm font-bold text-ink">{title}</h2>
      </div>
      {loading ? (
        <div className="mt-3 flex gap-2">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-14 flex-1" rounded="card" />
          ))}
        </div>
      ) : lockedText ? (
        <p className="mt-3 rounded-xl bg-negative-bg px-3 py-2.5 text-xs font-medium text-ink">
          {lockedText}
        </p>
      ) : code ? (
        <div className="mt-3 flex gap-2" aria-label={code.split('').join(' ')}>
          {code.split('').map((digit, index) => (
            <span
              key={index}
              className="inline-flex h-14 flex-1 items-center justify-center rounded-xl bg-brand-soft text-headline font-bold text-ink tabular"
            >
              {digit}
            </span>
          ))}
        </div>
      ) : failedText ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-negative">{failedText}</p>
          <Button size="sm" tone="outline" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      ) : null}
      <p className="mt-3 text-caption text-ink-2">{hint}</p>
    </Card>
  );
}

/** Leave-at-door proof photo after delivery (C25). */
export function ProofPhotoCard({
  title,
  caption,
  url,
}: {
  title: string;
  caption: string;
  url: string;
}) {
  return (
    <Card>
      <h2 className="text-sm font-bold text-ink">{title}</h2>
      <a href={url} target="_blank" rel="noreferrer" className="mt-3 block">
        <img
          src={url}
          alt={title}
          loading="lazy"
          className="aspect-4/3 w-full rounded-xl bg-tile object-cover"
        />
      </a>
      <p className="mt-2 text-caption text-ink-3">{caption}</p>
    </Card>
  );
}

export interface ChatBubble {
  id: string;
  body: string;
  time: string;
  mine: boolean;
  pending: boolean;
}

/**
 * Chat sheet (C24): bubbles (mine = yellow right with ink text, rider = tile left), quick-reply
 * pills, then a field + send button pinned at the bottom. Closed chats show a notice instead.
 */
export function ChatSheet({
  title,
  bubbles,
  loading,
  open,
  closedText,
  emptyText,
  quickReplies,
  text,
  maxLength,
  canSend,
  draftRestored,
  labels,
  onText,
  onSend,
  onQuick,
  onKeepDraft,
  onDiscardDraft,
  onClose,
}: {
  title: string;
  bubbles: ChatBubble[];
  loading: boolean;
  open: boolean;
  closedText: string;
  emptyText: string;
  quickReplies: Array<{ key: string; label: string }>;
  text: string;
  maxLength: number;
  canSend: boolean;
  draftRestored: boolean;
  labels: {
    placeholder: string;
    send: string;
    sending: string;
    draftRestored: string;
    keep: string;
    discard: string;
  };
  onText: (value: string) => void;
  onSend: () => void;
  onQuick: (key: string) => void;
  onKeepDraft: () => void;
  onDiscardDraft: () => void;
  onClose: () => void;
}) {
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [bubbles.length]);

  const footer = open ? (
    <div className="space-y-2.5">
      {quickReplies.length > 0 && (
        <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5">
          {quickReplies.map((reply) => (
            <button
              key={reply.key}
              type="button"
              onClick={() => onQuick(reply.key)}
              className="inline-flex h-8 shrink-0 items-center rounded-full bg-surface px-3 text-xs font-medium text-ink shadow-[inset_0_0_0_1px_var(--color-control)]"
            >
              {reply.label}
            </button>
          ))}
        </div>
      )}
      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSend();
        }}
      >
        <input
          value={text}
          maxLength={maxLength}
          onChange={(event) => onText(event.target.value)}
          placeholder={labels.placeholder}
          aria-label={labels.placeholder}
          className="h-11 min-w-0 flex-1 rounded-field bg-surface px-3 text-sm text-ink shadow-[inset_0_0_0_1px_var(--color-field)] placeholder:text-ink-3 focus:shadow-[inset_0_0_0_1.5px_var(--color-brand-600)] focus:outline-none"
        />
        <Button type="submit" disabled={!canSend} className="shrink-0">
          {labels.send}
        </Button>
      </form>
    </div>
  ) : (
    <p className="text-center text-xs text-ink-3">{closedText}</p>
  );

  return (
    <Sheet title={title} onClose={onClose} footer={footer}>
      {draftRestored && (
        <div className="mb-3">
          <DraftBar
            text={labels.draftRestored}
            keepLabel={labels.keep}
            discardLabel={labels.discard}
            onKeep={onKeepDraft}
            onDiscard={onDiscardDraft}
          />
        </div>
      )}
      {loading ? (
        <div className="space-y-3" role="status" aria-label={title}>
          <Skeleton className="h-10 w-2/3" rounded="card" />
          <Skeleton className="ml-auto h-10 w-1/2" rounded="card" />
          <Skeleton className="h-10 w-3/5" rounded="card" />
        </div>
      ) : bubbles.length === 0 ? (
        <p className="py-6 text-center text-xs text-ink-3">{emptyText}</p>
      ) : (
        <div className="flex flex-col gap-2" aria-live="polite">
          {bubbles.map((bubble) => (
            <div
              key={bubble.id}
              className={`max-w-4/5 rounded-2xl px-3.5 py-2 ${
                bubble.mine
                  ? 'self-end rounded-br-md bg-brand text-on-brand'
                  : 'self-start rounded-bl-md bg-tile text-ink'
              } ${bubble.pending ? 'opacity-70' : ''}`}
            >
              <p className="text-sm break-words whitespace-pre-wrap">{bubble.body}</p>
              <p
                className={`mt-0.5 text-right text-micro tabular ${bubble.mine ? 'text-on-brand/70' : 'text-ink-3'}`}
              >
                {bubble.pending ? labels.sending : bubble.time}
              </p>
            </div>
          ))}
          <div ref={end} />
        </div>
      )}
    </Sheet>
  );
}
